<template>
    <section class="pdh" v-if="post">
        <header class="pdh-meta-row">
            <div class="pdh-meta-left">
                <button type="button" class="pdh-back" @click="goBack">
                    <i class="fal fa-chevron-left"></i>
                    <span>{{ $t('common.back') || 'Retour' }}</span>
                </button>

                <span class="pdh-meta-sep" aria-hidden="true">·</span>

                <span class="pdh-ref">
                    <span>#{{ post.reference }}</span>
                    <button
                        type="button"
                        class="pdh-ref-copy"
                        v-clipboard:copy="post.reference || ''"
                        :title="$t('common.copy') || 'Copier'"
                        aria-label="Copier la référence"
                    >
                        <i class="fal fa-copy"></i>
                    </button>
                </span>

                <template v-if="post.form && post.form.name">
                    <span class="pdh-meta-sep" aria-hidden="true">·</span>
                    <span class="pdh-meta-item">{{ post.form.name }}</span>
                </template>

                <span class="pdh-meta-sep" aria-hidden="true">·</span>
                <span class="pdh-meta-item pdh-meta-muted">
                    <i class="fal fa-clock pdh-meta-icon" aria-hidden="true"></i>
                    {{ moment(post.created_at).fromNow() }}
                </span>
            </div>

            <div class="pdh-actions">
                <ButtonEditPriority variant="v2" :post="post" />

                <button
                    v-if="hasOperator"
                    type="button"
                    class="pdh-btn pdh-btn-warning"
                    :disabled="loading"
                    @click="sendRemindToOperator"
                >
                    <i v-if="loading" class="fas fa-circle-notch fa-spin"></i>
                    <i v-else class="fal fa-bell"></i>
                    <span>{{ $t('post.page.button.restart') || 'Relancer' }}</span>
                </button>

                <button
                    type="button"
                    class="pdh-btn pdh-btn-primary"
                    @click.prevent="exportPDF"
                >
                    <i v-if="loadingDownloadPdf" class="fas fa-circle-notch fa-spin"></i>
                    <i v-else class="fal fa-download"></i>
                    <span>{{ $t('post.page.button.export') || 'Exporter' }}</span>
                </button>

                <ButtonEditStatus variant="v2" :post="post" />

                <div class="pdh-more" v-click-outside="closeMenu">
                    <button
                        type="button"
                        class="pdh-btn pdh-btn-icon"
                        :aria-expanded="menuOpen ? 'true' : 'false'"
                        @click="menuOpen = !menuOpen"
                    >
                        <i class="fal fa-ellipsis-v"></i>
                    </button>
                    <div v-if="menuOpen" class="pdh-more-menu" role="menu">
                        <router-link :to="`/post/${post.id}/edit`" class="pdh-more-item" @click.native="closeMenu">
                            <i class="fal fa-edit"></i>
                            <span>{{ $t('post.page.button.edit') }}</span>
                        </router-link>
                        <!-- TODO: re-enable when backend supports answer duplication
                        <button
                            v-if="post.form && post.answers"
                            type="button"
                            class="pdh-more-item"
                            @click="duplicate"
                        >
                            <i class="fal fa-copy"></i>
                            <span>{{ $t('post.page.button.duplicate') }}</span>
                        </button>
                        -->
                    </div>
                </div>
            </div>
        </header>

        <h1 class="pdh-title">{{ post.title || $t('post.page.untitled') }}</h1>

        <div class="pdh-pills">
            <StatusBadge v-if="post.status" :status="post.status" />

            <span
                class="pdh-pill pdh-pill-priority"
                :class="`pdh-pill-priority--${post.priority || 0}`"
            >
                <i class="fal fa-flag"></i>
                {{ $t(`post.page.priority.${post.priority || 0}`) }}
            </span>

            <span v-if="post.address" class="pdh-pill">
                <i class="fal fa-map-marker-alt"></i>
                {{ post.address }}
            </span>

            <span v-if="post.form && post.form.name" class="pdh-pill">
                <i class="fal fa-tag"></i>
                {{ post.form.name }}
            </span>
        </div>
    </section>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import StatusBadge from '@/components/UI/StatusBadge'
import ButtonEditStatus from '@/components/UI/ButtonEditStatus'
import ButtonEditPriority from './ButtonEditPriority'

export default {
    name: 'PostDetailsHeader',
    props: ['post', 'loadingDownloadPdf'],
    components: { StatusBadge, ButtonEditStatus, ButtonEditPriority },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._outside = (e) => {
                    if (!el.contains(e.target)) binding.value()
                }
                document.addEventListener('mousedown', el._outside)
            },
            unbind(el) {
                document.removeEventListener('mousedown', el._outside)
            }
        }
    },
    data() {
        return {
            loading: false,
            menuOpen: false
        }
    },
    computed: {
        ...mapState('post', {
            hasOperator: (state) => state.hasOperator
        })
    },
    methods: {
        ...mapActions('post', ['remindOperator', 'getPostHistories']),
        goBack() {
            if (window.history.length > 1) this.$router.go(-1)
            else this.$router.push('/posts')
        },
        closeMenu() {
            this.menuOpen = false
        },
        exportPDF() {
            this.$emit('export-pdf')
        },
        duplicate() {
            this.closeMenu()
            this.$store.commit('wizard/setPrefillAnswers', this.post.answers || [])
            this.$router.push(`/post/create/forms/${this.post.form.id}/wizard`)
        },
        sendRemindToOperator() {
            this.loading = true
            this.remindOperator(this.post.id)
                .then(() => {
                    this.getPostHistories(this.post.id)
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('post.operator.reminded')
                    })
                })
                .catch((e) => this.$store.dispatch('dispatchError', e))
                .finally(() => {
                    setTimeout(() => { this.loading = false }, 2000)
                })
        }
    }
}
</script>

<style scoped>
.pdh {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    padding: var(--v2-space-5) var(--v2-space-6);
}

.pdh-meta-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    flex-wrap: wrap;
    margin-bottom: var(--v2-space-4);
}

.pdh-meta-left {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--v2-space-2);
    font-size: 0.85rem;
    color: var(--v2-text-3);
    min-width: 0;
}

.pdh-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 8px;
    border: 1px solid var(--v2-border);
    background: var(--v2-surface-0);
    border-radius: var(--v2-radius-sm);
    font: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    color: var(--v2-text-2);
    cursor: pointer;
    transition: background var(--v2-transition-fast), border-color var(--v2-transition-fast);
}

.pdh-back i {
    font-size: 0.7rem;
}

.pdh-back:hover {
    background: var(--v2-surface-2);
    border-color: var(--v2-border-strong, var(--v2-border));
}

.pdh-ref {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    font-variant-numeric: tabular-nums;
}

.pdh-ref-copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: none;
    background: transparent;
    color: var(--v2-text-4);
    cursor: pointer;
    border-radius: var(--v2-radius-xs, 4px);
    transition: color var(--v2-transition-fast), background var(--v2-transition-fast);
    font-size: 0.78rem;
    padding: 0;
}

.pdh-ref-copy:hover {
    color: var(--v2-primary);
    background: var(--v2-surface-2);
}

.pdh-meta-sep {
    color: var(--v2-text-4);
    font-size: 0.8rem;
    user-select: none;
}

.pdh-meta-item {
    color: var(--v2-text-2);
}

.pdh-meta-muted {
    color: var(--v2-text-3);
}

.pdh-meta-icon {
    font-size: 0.78rem;
    margin-right: 3px;
    color: var(--v2-text-4);
}

.pdh-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.pdh-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border-radius: var(--v2-radius-sm);
    border: 1px solid var(--v2-border);
    background: var(--v2-surface-0);
    font: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.2;
    color: var(--v2-text-2);
    cursor: pointer;
    white-space: nowrap;
    box-sizing: border-box;
    transition: background var(--v2-transition-fast),
        border-color var(--v2-transition-fast),
        color var(--v2-transition-fast);
    text-decoration: none !important;
}

.pdh-btn i {
    font-size: 0.8rem;
}

.pdh-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.pdh-btn-ghost:hover:not(:disabled) {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.pdh-btn-primary {
    background: var(--v2-primary);
    border-color: var(--v2-primary);
    color: var(--v2-primary-contrast, #fff) !important;
}

.pdh-btn-primary i {
    color: inherit !important;
}

.pdh-btn-primary:hover:not(:disabled) {
    background: var(--v2-primary-hover);
    border-color: var(--v2-primary-hover);
}

.pdh-btn-warning {
    background: var(--v2-warning);
    border-color: var(--v2-warning);
    color: #fff !important;
}

.pdh-btn-warning i {
    color: inherit !important;
}

.pdh-btn-warning:hover:not(:disabled) {
    filter: brightness(0.95);
}

.pdh-btn-icon {
    width: 32px;
    padding: 0;
    justify-content: center;
    color: var(--v2-text-3);
}

.pdh-btn-icon:hover:not(:disabled) {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.pdh-more {
    position: relative;
}

.pdh-more-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 180px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    box-shadow: var(--v2-shadow-lg);
    padding: 6px;
    z-index: 30;
}

.pdh-more-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    border-radius: var(--v2-radius-sm);
    font-size: 0.82rem;
    color: var(--v2-text-2);
    text-decoration: none !important;
    cursor: pointer;
}

.pdh-more-item:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.pdh-more-item i {
    font-size: 0.8rem;
    color: var(--v2-text-3);
}

.pdh-title {
    margin: 0 0 var(--v2-space-3);
    font-size: 1.5rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.25;
}

.pdh-pills {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--v2-space-2);
}

.pdh-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 22px;
    padding: 0 10px;
    border-radius: 999px;
    background: var(--v2-surface-2);
    color: var(--v2-text-2);
    font-size: 0.72rem;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.01em;
    white-space: nowrap;
    box-sizing: border-box;
}

.pdh-pill i {
    font-size: 0.7rem;
    color: var(--v2-text-3);
}

.pdh-pill-priority--0 { background: var(--v2-surface-2); color: var(--v2-text-3); }
.pdh-pill-priority--0 i { color: var(--v2-text-4); }
.pdh-pill-priority--1 { background: rgba(59, 130, 246, 0.12); color: #2563eb; }
.pdh-pill-priority--1 i { color: #2563eb; }
.pdh-pill-priority--2 { background: rgba(16, 185, 129, 0.12); color: #047857; }
.pdh-pill-priority--2 i { color: #047857; }
.pdh-pill-priority--3 { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.pdh-pill-priority--3 i { color: #b45309; }
.pdh-pill-priority--4 { background: rgba(239, 68, 68, 0.12); color: #b91c1c; }
.pdh-pill-priority--4 i { color: #b91c1c; }

@media (max-width: 900px) {
    .pdh {
        padding: var(--v2-space-4);
    }

    .pdh-meta-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .pdh-actions {
        width: 100%;
    }

    .pdh-btn span {
        display: none;
    }

    .pdh-btn {
        padding: 0 10px;
    }

    .pdh-btn-primary span {
        display: inline;
    }

    .pdh-title {
        font-size: 1.25rem;
    }
}
</style>
